import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { type Event, type EventId } from 'hb-react/shared/apps/Events';
import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import { axiosInstance as axios, isCancel } from 'hb-react/shared/utils/axios';
import { polymorphicTopicUrl } from 'hb-react/shared/utils/polymorphicTopicUrl';

export const useEvent = ({
  route,
  eventId: providedEventId,
}: {
  eventId?: EventId;
  route?: (id: EventId, options?: { format?: string }) => string;
} = {}) => {
  const topicContext = useTopicContext();
  const navigate = useNavigate();

  const { eventId: paramsEventId } = useParams();

  const numericEventId = Number(providedEventId || paramsEventId);
  const isValidEventId = !Number.isNaN(numericEventId) && numericEventId > 0;

  const adjustedRoute = dynamicTopicResourcePath(
    route && typeof route === 'function' && isValidEventId
      ? route(numericEventId, { format: 'json' })
      : polymorphicTopicUrl('event', {
          isTopic: !!topicContext.topicId,
          urlParams: [topicContext.topicId, numericEventId, { format: 'json' }],
        }),
    topicContext,
  );

  const adjustedIndexRoute = dynamicTopicResourcePath(
    Routes.events_path(),
    topicContext,
  );

  const params = topicContext.topicId ? { topic_id: topicContext.topicId } : {};

  const { isLoading, data: event } = useQuery({
    queryKey: ['event', numericEventId, params],
    queryFn: () =>
      axios
        .get<{ event: Event }>(adjustedRoute, { params })
        .then((response) => response.data.event)
        .catch((error: any) => {
          if (isCancel(error)) {
            return undefined;
          }

          toast.error(
            s__(
              'Event|The event you tried to view does not exist or is not available.',
            ),
          );
          navigate(adjustedIndexRoute);
        }),
    staleTime: ms('5m'),
    refetchOnMount: false,
    enabled: isValidEventId,
  });

  return { isLoading, event };
};
